/*
 * DAT-906 · "Back to wage breakdown": the history-state contract between a shift's wage breakdown and the
 * pages its links open (Team → Wage Settings / Agency Configuration, the nurse's profile).
 *
 * Carried in `location.state`, never the URL: it describes how the user arrived, which a pasted link, a
 * refresh or any other entry point must not reproduce. Each page reads its key once on arrival and removes
 * it from the history entry (`useArrivalState`).
 */

/** Set by a breakdown link on the page it opens: where the breakdown was, so the page can offer the way back. */
export const FROM_WAGE_BREAKDOWN_STATE_KEY = "fromWageBreakdown";
/** Set by "Back to wage breakdown" on the day schedule: which shift's breakdown to open again. */
export const REOPEN_WAGE_BREAKDOWN_STATE_KEY = "reopenWageBreakdown";

export type WageBreakdownReopen = {
	/** The nurse-in-shift id whose row the breakdown hangs under. */
	shiftId: string;
	/** Whether it was the Actual view's breakdown (punched hours) rather than the Scheduled one. */
	isActual: boolean;
	/** Whether its rule lines were unfolded when the link was followed: it reopens the same way. */
	detailsOpen: boolean;
};

/** Where a breakdown link led, as `wage_breakdown_return_clicked` reports it. */
export type WageBreakdownDestination = "wage_settings" | "agency_configuration" | "nurse_profile";

const WAGE_BREAKDOWN_DESTINATIONS: readonly WageBreakdownDestination[] = ["wage_settings", "agency_configuration", "nurse_profile"];

export type WageBreakdownOrigin = WageBreakdownReopen & {
	/** The day schedule's path and query, as the user left it. */
	returnTo: string;
	/** The page the link opened, set by the link itself: what the page shows now may be another tab. */
	destination: WageBreakdownDestination;
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

/** One key of a history entry's state; `undefined` when the state is not an object or lacks it. */
export const readStateKey = (state: unknown, key: string): unknown => (isRecord(state) ? state[key] : undefined);

/** The state with one key removed, every other key (e.g. `navigationSrc`) kept. `null` when nothing is left. */
export const stateWithoutKey = (state: unknown, key: string): Record<string, unknown> | null => {
	if (!isRecord(state)) return null;
	const rest = Object.fromEntries(Object.entries(state).filter(([entryKey]) => entryKey !== key));
	return Object.keys(rest).length > 0 ? rest : null;
};

export const parseWageBreakdownReopen = (value: unknown): WageBreakdownReopen | undefined => {
	if (!isRecord(value)) return undefined;
	const { shiftId, isActual, detailsOpen } = value;
	if (typeof shiftId !== "string" || shiftId === "" || typeof isActual !== "boolean" || typeof detailsOpen !== "boolean") return undefined;
	return { shiftId, isActual, detailsOpen };
};

/** Only a same-app path is a place to return to: anything else is not a breakdown's origin. */
const isAppPath = (value: unknown): value is string => typeof value === "string" && value.startsWith("/") && !value.startsWith("//");

const isDestination = (value: unknown): value is WageBreakdownDestination =>
	typeof value === "string" && WAGE_BREAKDOWN_DESTINATIONS.some((destination) => destination === value);

export const parseWageBreakdownOrigin = (value: unknown): WageBreakdownOrigin | undefined => {
	const reopen = parseWageBreakdownReopen(value);
	const returnTo = readStateKey(value, "returnTo");
	const destination = readStateKey(value, "destination");
	if (reopen === undefined || !isAppPath(returnTo) || !isDestination(destination)) return undefined;
	return { ...reopen, returnTo, destination };
};
